import React, { useEffect, useMemo, useState } from 'react'
import { Box, Typography, Collapse, IconButton } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import IntelLayout from './components/IntelLayout'
import KpiCard from './components/KpiCard'
import Badge from './components/Badge'
import { intelApi } from './api'
import { intelColors, formatNumber } from './components/intelTheme'

const categoryColor = (c) => {
  switch (c) {
    case 'FORWARD':
      return 'blue'
    case 'REVERSE':
      return 'orange'
    case 'HIGH_VALUE':
      return 'purple'
    case 'SDD_NDD':
      return 'green'
    case 'HYPERLOCAL':
      return 'grey'
    default:
      return 'grey'
  }
}

const CATEGORY_LABEL = { FORWARD: 'Forward', REVERSE: 'Reverse', HIGH_VALUE: 'High Value', SDD_NDD: 'SDD / NDD', HYPERLOCAL: 'Hyperlocal' }
const CATEGORY_ORDER = ['FORWARD', 'REVERSE', 'HIGH_VALUE', 'SDD_NDD', 'HYPERLOCAL']
const dirLabel = (d) => (d === 'RVP' ? 'Reverse' : d === 'FORWARD' ? 'Forward' : d || '—')

function GroupCard({ group }) {
  const [open, setOpen] = useState(false)
  return (
    <Box
      sx={{
        background: '#fff',
        border: `1px solid ${intelColors.border}`,
        borderRadius: '8px',
        p: 2,
        mb: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: intelColors.text }}>{group.name}</Typography>
            <code style={{ background: '#f1f3f5', padding: '2px 6px', borderRadius: 3, fontSize: 11 }}>{group.code}</code>
            <Badge color={group.direction === 'RVP' ? 'orange' : 'blue'}>{dirLabel(group.direction)}</Badge>
            {group.mode && <Badge color={group.mode === 'AIR' ? 'purple' : group.mode === 'HYPERLOCAL' ? 'grey' : 'blue'}>{group.mode}</Badge>}
            {group.weightBand && <Badge color='grey'>{group.weightBand}</Badge>}
          </Box>
          <Typography sx={{ fontSize: 12, color: intelColors.subtext, mb: 1 }}>{group.description}</Typography>
          {group.routesVia && (
            <Typography sx={{ fontSize: 11, color: intelColors.purple }}>↳ Routes via <code>{group.routesVia}</code></Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box>
            <Typography sx={{ fontSize: 10, color: '#888', textTransform: 'uppercase', fontWeight: 700 }}>Codes</Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: intelColors.text }}>{group.codeCount ?? 0}</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 10, color: '#888', textTransform: 'uppercase', fontWeight: 700 }}>Pickup Pincodes</Typography>
            <Typography sx={{ fontSize: 14, color: intelColors.text }}>{formatNumber(group.pickupPinCount)}</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 10, color: '#888', textTransform: 'uppercase', fontWeight: 700 }}>Delivery Pincodes</Typography>
            <Typography sx={{ fontSize: 14, color: intelColors.text }}>{formatNumber(group.deliveryPinCount)}</Typography>
          </Box>
          <IconButton size='small' onClick={() => setOpen((o) => !o)}>
            {open ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={open}>
        <Box sx={{ mt: 2, borderTop: '1px solid #f0f0f0', pt: 1.5 }}>
          {(group.linkedCodes || []).length === 0 ? (
            <Typography sx={{ fontSize: 12, color: intelColors.muted }}>No linked courier codes</Typography>
          ) : (
            <>
              <Typography sx={{ fontSize: 10, color: '#888', textTransform: 'uppercase', fontWeight: 700, mb: 0.5 }}>
                {group.linkedCodes.length} linked courier code{group.linkedCodes.length === 1 ? '' : 's'}
              </Typography>
              {/* Scroll within the card so a popular ST with many codes doesn't push the page. */}
              <Box sx={{ maxHeight: 300, overflowY: 'auto', pr: 0.5 }}>
                {group.linkedCodes.map((c) => (
                  <Box key={c.cpAccountCode} sx={{ display: 'flex', gap: 2, alignItems: 'center', p: '4px 0', fontSize: 12, borderBottom: '1px solid #f8f8f8' }}>
                    <code style={{ background: '#f1f3f5', padding: '1px 5px', borderRadius: 3, fontSize: 11 }}>{c.cpAccountCode}</code>
                    <span style={{ color: intelColors.subtext }}>{c.parent}</span>
                    <Badge color={c.direction === 'RVP' ? 'orange' : 'blue'}>{dirLabel(c.direction)}</Badge>
                    <span style={{ color: intelColors.muted, fontSize: 11 }}>min {c.minChargeableWeight}kg · max {c.maxWeightSinglePiece}kg · vol /{c.volumetricDivisor}</span>
                    {c.isActive ? <Badge color='green'>Active</Badge> : <Badge color='grey'>Inactive</Badge>}
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

export default function SetupGroups() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    let cancel = false
    setLoading(true)
    setErr(null)
    intelApi
      .setupGroups()
      .then((res) => !cancel && setData(res?.data?.responseObj || res?.data?.data || res?.data))
      .catch((e) => !cancel && setErr(e?.response?.data?.exception || e.message))
      .finally(() => !cancel && setLoading(false))
    return () => {
      cancel = true
    }
  }, [])

  const kpis = data?.kpis || {}
  const groups = data?.groups || []

  const grouped = useMemo(() => {
    const map = {}
    groups.forEach((g) => {
      const k = g.category || 'OTHER'
      if (!map[k]) map[k] = []
      map[k].push(g)
    })
    return map
  }, [groups])

  return (
    <IntelLayout title='Service Type Groups'>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '14px', mb: 2 }}>
        <KpiCard loading={loading} color='blue' label='ST Groups' value={formatNumber(kpis.stGroups?.value)} />
        <KpiCard loading={loading} color='green' label='Codes Mapped' value={formatNumber(kpis.codesMapped?.value)} />
        <KpiCard loading={loading} color='orange' label='Courier Partners' value={formatNumber(kpis.courierPartners?.value)} />
        <KpiCard loading={loading} color='purple' label='Groups with Codes' value={formatNumber(kpis.groupsWithCodes?.value)} />
        <KpiCard loading={loading} color='blue' label='Forward Groups (incl. SDD/NDD)' value={formatNumber(kpis.fwdGroups?.value)} />
        <KpiCard loading={loading} color='orange' label='Reverse Groups' value={formatNumber(kpis.revGroups?.value)} />
      </Box>

      {err && (
        <Box sx={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', borderRadius: '6px', p: '10px 14px', mb: '14px', fontSize: 12 }}>
          {err}
        </Box>
      )}

      {loading && <Typography sx={{ fontSize: 12, color: intelColors.muted }}>Loading…</Typography>}

      {[...CATEGORY_ORDER.filter((c) => grouped[c]), ...Object.keys(grouped).filter((c) => !CATEGORY_ORDER.includes(c))].map((cat) => {
        const gs = grouped[cat]
        return (
          <Box key={cat} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: intelColors.text }}>{CATEGORY_LABEL[cat] || cat}</Typography>
              <Badge color={categoryColor(cat)}>{gs.length}</Badge>
              <Box sx={{ flex: 1, height: 1, background: intelColors.border }} />
            </Box>
            {gs.map((g) => <GroupCard key={g.code} group={g} />)}
          </Box>
        )
      })}
    </IntelLayout>
  )
}
